import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  RefreshCw,
  RotateCw,
  ServerCrash,
  Trash2,
} from "lucide-react";
import {
  useReconciliationReport,
  useSoftDeleteOrphanDB,
  useImportOrphanProxmox,
  useDestroyOrphanProxmox,
  useSyncDrift,
} from "@/hooks/useReconciliation";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ImportOrphanModal } from "@/components/reconciliation/ImportOrphanModal";
import type {
  OrphanInDB,
  OrphanInProxmox,
  StatusDrift,
} from "@/api/endpoints/reconciliation";

export default function Reconciliation() {
  const { data: report, isLoading, isFetching, refetch, error } =
    useReconciliationReport();
  const softDelete = useSoftDeleteOrphanDB();
  const importOrphan = useImportOrphanProxmox();
  const destroyOrphan = useDestroyOrphanProxmox();
  const syncDrift = useSyncDrift();

  const [importTarget, setImportTarget] = useState<OrphanInProxmox | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">
            DB &harr; Proxmox reconciliation
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Detects VMs that exist on one side but not the other. A misclick
            ``qm destroy`` on a node, an aborted import or a network blip
            during a delete will show up here.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800 disabled:opacity-50"
        >
          <RefreshCw className={isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          Re-scan
        </button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="rounded-xl border border-red-700 bg-red-900/20 p-4 text-sm text-red-300">
          Scan failed: {(error as Error).message}
        </div>
      ) : report ? (
        <>
          <SummaryCard report={report} />
          {report.errors.length > 0 && (
            <UnreachableCard errors={report.errors} />
          )}

          <Section
            title="Orphans in DB"
            description="VM in virtual_machines whose (node, vmid) is not in qm list. The safest action is Move to trash so the purge job cleans the row in 30 days; nothing on Proxmox is touched."
            count={report.orphans_in_db.length}
          >
            {report.orphans_in_db.length === 0 ? (
              <Empty />
            ) : (
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900/50 text-xs uppercase text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">DB id</th>
                    <th className="px-4 py-3 text-left">VMID</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Node</th>
                    <th className="px-4 py-3 text-left">DB status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700 text-sm text-gray-200">
                  {report.orphans_in_db.map((o) => (
                    <OrphanInDBRow
                      key={`db-${o.db_id}`}
                      orphan={o}
                      onSoftDelete={() => softDelete.mutate(o.db_id)}
                      pending={
                        softDelete.isPending && softDelete.variables === o.db_id
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section
            title="Orphans in Proxmox"
            description="VM running on Proxmox without a row in virtual_machines. Templates (vmid >= 9000) are excluded. Prefer Import for real workloads; Destroy is irreversible."
            count={report.orphans_in_proxmox.length}
          >
            {report.orphans_in_proxmox.length === 0 ? (
              <Empty />
            ) : (
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900/50 text-xs uppercase text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">Node</th>
                    <th className="px-4 py-3 text-left">VMID</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">PVE status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700 text-sm text-gray-200">
                  {report.orphans_in_proxmox.map((o) => (
                    <OrphanInProxmoxRow
                      key={`pve-${o.node}-${o.vmid}`}
                      orphan={o}
                      onImportClick={() => setImportTarget(o)}
                      onDestroy={() => {
                        const confirmation = window.prompt(
                          `PERMANENT destroy of ${o.name} (vmid ${o.vmid}) on ${o.node}.\n\nRetype the VM name to confirm:`,
                        );
                        if (confirmation === o.name) {
                          destroyOrphan.mutate({ node: o.node, vmid: o.vmid });
                        } else if (confirmation !== null) {
                          alert("Name did not match — destroy cancelled.");
                        }
                      }}
                      destroyPending={
                        destroyOrphan.isPending &&
                        destroyOrphan.variables?.vmid === o.vmid
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section
            title="Status drift"
            description="VM exists on both sides but the status diverges. Sync overwrites the DB with what Proxmox reports; Proxmox is treated as the source of truth."
            count={report.drifts.length}
          >
            {report.drifts.length === 0 ? (
              <Empty />
            ) : (
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900/50 text-xs uppercase text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">DB id</th>
                    <th className="px-4 py-3 text-left">VMID</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Node</th>
                    <th className="px-4 py-3 text-left">DB</th>
                    <th className="px-4 py-3 text-left">Proxmox</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700 text-sm text-gray-200">
                  {report.drifts.map((d) => (
                    <DriftRow
                      key={`drift-${d.db_id}`}
                      drift={d}
                      onSync={() => syncDrift.mutate(d.db_id)}
                      pending={
                        syncDrift.isPending && syncDrift.variables === d.db_id
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <p className="text-xs text-gray-500">
            Scanned {report.nodes.join(", ")} at{" "}
            {new Date(report.scanned_at).toLocaleString()}.
          </p>
        </>
      ) : null}

      {importTarget && (
        <ImportOrphanModal
          open={!!importTarget}
          node={importTarget.node}
          vmid={importTarget.vmid}
          vmName={importTarget.name}
          onClose={() => setImportTarget(null)}
          onConfirm={(tenantId, serviceTier) => {
            const target = importTarget;
            importOrphan.mutate(
              {
                node: target.node,
                vmid: target.vmid,
                tenantId,
                serviceTier,
              },
              { onSuccess: () => setImportTarget(null) },
            );
          }}
          loading={importOrphan.isPending}
        />
      )}
    </div>
  );
}

function OrphanInDBRow({
  orphan,
  onSoftDelete,
  pending,
}: {
  orphan: OrphanInDB;
  onSoftDelete: () => void;
  pending: boolean;
}) {
  return (
    <tr>
      <td className="px-4 py-2 font-mono">{orphan.db_id}</td>
      <td className="px-4 py-2 font-mono">{orphan.vmid}</td>
      <td className="px-4 py-2">{orphan.name}</td>
      <td className="px-4 py-2">{orphan.node}</td>
      <td className="px-4 py-2">{orphan.db_status}</td>
      <td className="px-4 py-2 text-right">
        <button
          onClick={onSoftDelete}
          disabled={pending}
          title="Move to trash (restorable 30 days)"
          className="inline-flex items-center gap-2 rounded-lg border border-yellow-700 bg-yellow-900/30 px-3 py-1.5 text-xs font-medium text-yellow-200 transition-colors hover:bg-yellow-900/60 disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Move to trash
        </button>
      </td>
    </tr>
  );
}

function OrphanInProxmoxRow({
  orphan,
  onImportClick,
  onDestroy,
  destroyPending,
}: {
  orphan: OrphanInProxmox;
  onImportClick: () => void;
  onDestroy: () => void;
  destroyPending: boolean;
}) {
  return (
    <tr>
      <td className="px-4 py-2">{orphan.node}</td>
      <td className="px-4 py-2 font-mono">{orphan.vmid}</td>
      <td className="px-4 py-2">{orphan.name}</td>
      <td className="px-4 py-2">{orphan.pve_status}</td>
      <td className="px-4 py-2 text-right">
        <div className="inline-flex items-center gap-2">
          <button
            onClick={onImportClick}
            title="Import as a new virtual_machines row"
            className="inline-flex items-center gap-2 rounded-lg border border-blue-700 bg-blue-900/30 px-3 py-1.5 text-xs font-medium text-blue-200 transition-colors hover:bg-blue-900/60"
          >
            <Download className="h-3.5 w-3.5" />
            Import
          </button>
          <button
            onClick={onDestroy}
            disabled={destroyPending}
            title="Stop + destroy on Proxmox (irreversible)"
            className="inline-flex items-center gap-2 rounded-lg border border-red-700 bg-red-900/30 px-3 py-1.5 text-xs font-medium text-red-200 transition-colors hover:bg-red-900/60 disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Destroy
          </button>
        </div>
      </td>
    </tr>
  );
}

function DriftRow({
  drift,
  onSync,
  pending,
}: {
  drift: StatusDrift;
  onSync: () => void;
  pending: boolean;
}) {
  return (
    <tr>
      <td className="px-4 py-2 font-mono">{drift.db_id}</td>
      <td className="px-4 py-2 font-mono">{drift.vmid}</td>
      <td className="px-4 py-2">{drift.name}</td>
      <td className="px-4 py-2">{drift.node}</td>
      <td className="px-4 py-2 text-yellow-300">{drift.db_status}</td>
      <td className="px-4 py-2 text-blue-300">{drift.pve_status}</td>
      <td className="px-4 py-2 text-right">
        <button
          onClick={onSync}
          disabled={pending}
          title="Re-pull live status from Proxmox"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-600 bg-gray-700 px-3 py-1.5 text-xs font-medium text-gray-200 transition-colors hover:bg-gray-600 disabled:opacity-40"
        >
          <RotateCw className={pending ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
          Sync
        </button>
      </td>
    </tr>
  );
}

function SummaryCard({
  report,
}: {
  report: { total_issues: number; nodes: string[] };
}) {
  const ok = report.total_issues === 0;
  return (
    <div
      className={
        "flex items-center gap-3 rounded-xl border p-4 " +
        (ok
          ? "border-green-700 bg-green-900/20 text-green-300"
          : "border-yellow-700 bg-yellow-900/20 text-yellow-200")
      }
    >
      {ok ? (
        <CheckCircle2 className="h-6 w-6" />
      ) : (
        <AlertTriangle className="h-6 w-6" />
      )}
      <div>
        <div className="font-semibold">
          {ok
            ? `In sync — ${report.nodes.length} node(s) scanned, no drift`
            : `${report.total_issues} drift(s) detected across ${report.nodes.length} node(s)`}
        </div>
      </div>
    </div>
  );
}

function UnreachableCard({ errors }: { errors: string[] }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-orange-700 bg-orange-900/20 p-4 text-sm text-orange-200">
      <ServerCrash className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="space-y-1">
        <div className="font-semibold">Unreachable nodes (skipped)</div>
        <ul className="list-disc pl-5">
          {errors.map((e, i) => (
            <li key={i} className="font-mono text-xs">
              {e}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  count,
  children,
}: {
  title: string;
  description: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-700 bg-gray-800">
      <div className="border-b border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-100">{title}</h2>
          <span
            className={
              "rounded-full px-2 py-0.5 text-xs font-medium " +
              (count === 0
                ? "bg-gray-700 text-gray-300"
                : "bg-yellow-700 text-yellow-100")
            }
          >
            {count}
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-400">{description}</p>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function Empty() {
  return (
    <div className="px-4 py-6 text-center text-sm text-gray-500">
      Nothing to show — clean.
    </div>
  );
}
